import * as React from 'react';
import { Drawer, IconButton, Popover, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import Wallpaper from '@material-ui/icons/Wallpaper';
import Close from '@material-ui/icons/Close';
import clsx from 'clsx';
import { XXL_COLORS } from '../../theme/colors';
import { OptionClassificationIdentifiers } from '../../Enums';
import ComponentOptionsSection from '../Sidebar/ComponentOptionsSection';
import { editorSidebarPaperProps } from './useDeselectEditorOnClickAway';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';

const useStyles = makeStyles((theme) => {
  const tokens = (theme as { xxlprint?: typeof XXL_COLORS }).xxlprint || XXL_COLORS;
  return {
    root: {
      position: 'absolute',
      bottom: theme.spacing(2),
      left: 0,
      right: 0,
      zIndex: 5,
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(0, 2),
      // Float over the canvas without blocking it — only the control catches clicks.
      pointerEvents: 'none',
      '& > *': { pointerEvents: 'auto' },
    },
    trigger: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 1.75),
      border: `1px solid ${tokens.borderStrong}`,
      backgroundColor: tokens.paperBackground,
      boxShadow: '0 3px 14px rgba(12,12,12,0.14)',
      cursor: 'pointer',
      color: tokens.sectionTitle,
      fontFamily: 'inherit',
      fontWeight: 700,
      fontSize: 13,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      transition: 'color 0.12s ease, background-color 0.12s ease, border-color 0.12s ease',
      '&:hover': { backgroundColor: tokens.surfaceMuted, borderColor: tokens.accent },
    },
    triggerOpen: {
      color: tokens.accent,
      borderColor: tokens.accent,
    },
    icon: {
      flexShrink: 0,
      color: 'inherit',
    },
    label: {
      whiteSpace: 'nowrap',
    },
    // Shared panel chrome: a fixed header row above a scrolling body, so the close
    // control stays reachable no matter how many backgrounds are listed.
    panelPaper: {
      display: 'flex',
      flexDirection: 'column',
      border: `1px solid ${tokens.borderStrong}`,
      boxShadow: '0 6px 24px rgba(12,12,12,0.18)',
    },
    popoverPaper: {
      borderRadius: 0,
      // Explicit responsive width so the card grid can lay out multiple columns (an
      // auto-width popover would collapse the grid to a single column).
      width: 'min(720px, calc(100vw - 24px))',
      maxHeight: 'min(72vh, 520px)',
    },
    // Mobile: a bottom sheet pinned full-width to the bottom edge.
    sheetPaper: {
      width: '100%',
      maxWidth: '100%',
      maxHeight: '85vh',
      borderRadius: 0,
      borderLeft: 'none',
      borderRight: 'none',
      borderBottom: 'none',
    },
    header: {
      flex: '0 0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 1, 1, 2),
      borderBottom: `1px solid ${tokens.borderDefault}`,
      backgroundColor: tokens.paperBackground,
    },
    heading: {
      fontWeight: 700,
      fontSize: 14,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: tokens.sectionTitle,
    },
    closeButton: {
      color: tokens.sectionTitle,
    },
    scrollArea: {
      flex: '1 1 auto',
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    content: {
      padding: theme.spacing(2),
      // Center the option tiles within the panel (incl. a partial last row).
      '& [class*="imageCards"]': {
        justifyContent: 'center',
      },
    },
  };
});

export default function BackgroundPanel() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);

  const close = React.useCallback(() => setOpen(false), []);

  const panel = (
    <>
      <div className={classes.header}>
        <span className={classes.heading}>{t('backgroundPanel.heading')}</span>
        <IconButton
          size="small"
          className={classes.closeButton}
          aria-label={t('backgroundPanel.close')}
          onClick={close}
        >
          <Close fontSize="small" />
        </IconButton>
      </div>
      <div className={classes.scrollArea}>
        <div className={classes.content}>
          <ComponentOptionsSection classificationId={OptionClassificationIdentifiers.background} />
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className={classes.root} data-editor-sidebar>
        <button
          ref={buttonRef}
          type="button"
          className={clsx(classes.trigger, open && classes.triggerOpen)}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <Wallpaper fontSize="small" className={classes.icon} />
          <span className={classes.label}>{t('backgroundPanel.title')}</span>
        </button>
      </div>

      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={close}
          PaperProps={editorSidebarPaperProps({ className: clsx(classes.panelPaper, classes.sheetPaper) })}
        >
          {panel}
        </Drawer>
      ) : (
        <Popover
          open={open}
          anchorEl={buttonRef.current}
          onClose={close}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          // This configurator renders embedded, where window.innerHeight can report 0.
          // That breaks MUI's viewport-overflow heuristic (false "too tall" warnings +
          // a bogus maxHeight), so disable it and constrain the paper ourselves above.
          marginThreshold={null}
          PaperProps={editorSidebarPaperProps({ className: clsx(classes.panelPaper, classes.popoverPaper) })}
        >
          {panel}
        </Popover>
      )}
    </>
  );
}
